<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import MessageAPI from '../../../../api/inbox/message';
import PipelinesAPI from 'dashboard/api/pipelines';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { emitter } from 'shared/helpers/mitt';
import Button from '../../../../components-next/button/Button.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
});
const emit = defineEmits(['close']);
const store = useStore();
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

const { t } = useI18n();

const loading = ref(false);
const sendingMessage = ref(false);
const templates = ref([]);
const selectedTemplate = ref(null);
const selectedConditions = ref({
  status: '',
  priority: '',
  hasAttachments: false,
  stage: '',
  assignee: '',
  lastActivity: '',
  tags: [],
  customFields: [],
});
const pipelineCards = ref([]);
const selectedConversations = ref(new Set());
const isProcessing = ref(false);
const processedItems = ref([]);
const successCount = ref(0);
const errorCount = ref(0);
const progress = ref(0);
const showFilters = ref(false);

// Buscar templates disponíveis
const fetchTemplates = async () => {
  try {
    loading.value = true;
    const response = await PipelinesAPI.get();
    const allTemplates = [];

    response.data.forEach(pipeline => {
      Object.values(pipeline.stages).forEach(stage => {
        if (stage.message_templates) {
          allTemplates.push(...stage.message_templates);
        }
      });
    });

    templates.value = allTemplates;
  } catch (error) {
    // Error handled silently
  } finally {
    loading.value = false;
  }
};

// Buscar itens do Pipeline
const fetchPipelineCards = async () => {
  try {
    if (!selectedPipeline.value?.id) return;

    const response = await PipelineCardsAPI.getCards(selectedPipeline.value.id);

    // Handle paginated response format (data.data) vs legacy (data as array)
    const items = response.data?.data || response.data;

    // Filtra apenas os itens que correspondem aos IDs recebidos
    pipelineCards.value = items.filter(item =>
      props.items.some(selectedItem => selectedItem.id === item.id)
    );
  } catch (error) {
    // Error loading pipeline cards
  }
};

// Computed para itens filtrados com todas as condições
const filteredItems = computed(() => {
  return pipelineCards.value.filter(item => {
    // Verifica se tem conversation_id
    if (!item.item_details?.conversation_id) return false;

    // Filtro por status
    if (
      selectedConditions.value.status &&
      item.item_details.status !== selectedConditions.value.status
    ) {
      return false;
    }

    // Filtro por prioridade
    if (
      selectedConditions.value.priority &&
      item.item_details.priority !== selectedConditions.value.priority
    ) {
      return false;
    }

    // Filtro por anexos
    if (selectedConditions.value.hasAttachments && !item.attachments?.length) {
      return false;
    }

    // Filtro por etapa
    if (
      selectedConditions.value.stage &&
      item.pipeline_stage !== selectedConditions.value.stage
    ) {
      return false;
    }

    // Filtro por responsável
    if (
      selectedConditions.value.assignee &&
      item.item_details.agent_id !== selectedConditions.value.assignee
    ) {
      return false;
    }

    // Filtro por última atividade
    if (selectedConditions.value.lastActivity) {
      const lastActivity = new Date(
        item.item_details.conversation?.last_activity_at
      );
      const now = new Date();
      const diffDays = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));

      switch (selectedConditions.value.lastActivity) {
        case 'today':
          if (diffDays > 0) return false;
          break;
        case 'week':
          if (diffDays > 7) return false;
          break;
        case 'month':
          if (diffDays > 30) return false;
          break;
        default:
          break;
      }
    }

    return true;
  });
});

const handleToggleConversation = item => {
  const id = item.item_details.conversation_id;
  if (selectedConversations.value.has(id)) {
    selectedConversations.value.delete(id);
  } else {
    selectedConversations.value.add(id);
  }
};

const handleSelectAll = () => {
  if (selectedConversations.value.size === filteredItems.value.length) {
    selectedConversations.value.clear();
  } else {
    selectedConversations.value = new Set(
      filteredItems.value.map(item => item.item_details.conversation_id)
    );
  }
};

const handleSend = async () => {
  if (!selectedTemplate.value || selectedConversations.value.size === 0) return;

  try {
    isProcessing.value = true;
    sendingMessage.value = true;
    processedItems.value = [];
    successCount.value = 0;
    errorCount.value = 0;
    progress.value = 0;

    const itemsToProcess = filteredItems.value.filter(item =>
      selectedConversations.value.has(item.item_details.conversation_id)
    );

    // Sequential awaits intentional: WhatsApp providers rate-limit
    // bulk sends, and we want predictable progress + per-item failure
    // accounting (Promise.all would mask which one failed).
    /* eslint-disable no-restricted-syntax, no-await-in-loop, no-plusplus */
    for (const item of itemsToProcess) {
      try {
        const conversationId = item.item_details.conversation_id;
        const inboxId = item.item_details.conversation?.inbox_id || 1;

        await MessageAPI.create({
          conversationId,
          message: selectedTemplate.value.content,
          private: false,
          message_type: 'outgoing',
          inbox_id: inboxId,
        });

        processedItems.value.push({
          id: item.id,
          title: item.item_details.title,
          conversationId,
          status: 'success',
        });
        successCount.value++;
      } catch (error) {
        processedItems.value.push({
          id: item.id,
          title: item.item_details.title,
          conversationId: item.item_details.conversation_id,
          status: 'error',
          error: error.message,
        });
        errorCount.value++;
      }

      progress.value =
        (processedItems.value.length / itemsToProcess.length) * 100;
    }
    /* eslint-enable no-restricted-syntax, no-await-in-loop, no-plusplus */

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.PROCESS_COMPLETED', {
        success: successCount.value,
        errors: errorCount.value,
      }),
      action: { type: successCount.value > 0 ? 'success' : 'error' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.PROCESS_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    sendingMessage.value = false;
  }
};

const handleClose = () => {
  if (isProcessing.value) {
    isProcessing.value = false;
    processedItems.value = [];
    successCount.value = 0;
    errorCount.value = 0;
    progress.value = 0;
  }
  emit('close');
};

onMounted(() => {
  fetchTemplates();
  fetchPipelineCards();
});
</script>

<template>
  <div class="space-y-6">
    <!-- Tela de Progresso -->
    <div v-if="isProcessing" class="space-y-6">
      <div class="text-center space-y-2">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_TITLE') }}
        </h3>
        <p class="text-body-main text-n-slate-11">
          {{
            t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_DESCRIPTION', {
              count: processedItems.length,
              total: selectedConversations.size,
            })
          }}
        </p>
      </div>

      <!-- Barra de Progresso -->
      <div class="relative pt-1">
        <div class="flex mb-2 items-center justify-between">
          <div>
            <span
              class="text-label-small font-semibold inline-block py-1 px-2 rounded-full"
              :class="{
                'text-n-teal-11 bg-n-teal-3': progress === 100,
                'text-n-brand bg-n-alpha-2': progress < 100,
              }"
            >
              {{ Math.round(progress) }}%
            </span>
          </div>
          <div class="text-right">
            <span
              class="text-label-small font-semibold inline-block text-n-slate-12"
            >
              {{ successCount }}
              {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.SUCCESS') }} •
              {{ errorCount }}
              {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.ERROR') }}
            </span>
          </div>
        </div>
        <div class="overflow-hidden h-2 mb-4 text-xs flex rounded bg-n-alpha-2">
          <div
            :style="{ width: `${progress}%` }"
            class="transition-all duration-500 shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center"
            :class="{
              'bg-n-teal-9': progress === 100,
              'bg-n-brand': progress < 100,
            }"
          />
        </div>
      </div>

      <!-- Lista de Resultados -->
      <div
        class="border border-n-weak rounded-lg divide-y divide-n-weak max-h-[400px] overflow-y-auto"
      >
        <div
          v-for="item in processedItems"
          :key="item.id"
          class="p-3 flex items-center justify-between hover:bg-n-alpha-1"
        >
          <div class="flex items-center gap-3">
            <span class="flex-shrink-0">
              <fluent-icon
                :icon="
                  item.status === 'success'
                    ? 'checkmark-circle'
                    : 'error-circle'
                "
                size="20"
                :class="
                  item.status === 'success'
                    ? 'text-n-teal-11'
                    : 'text-n-ruby-11'
                "
              />
            </span>
            <div class="flex-1">
              <p class="text-label font-medium text-n-slate-12">
                {{ item.title }}
              </p>
              <p class="text-label-small text-n-slate-11">
                {{
                  t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID', {
                    id: item.conversationId,
                  })
                }}
              </p>
              <p v-if="item.error" class="text-label-small text-n-ruby-11 mt-1">
                {{ item.error }}
              </p>
            </div>
          </div>
          <span
            class="text-label-small px-2 py-1 rounded-full"
            :class="
              item.status === 'success'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-ruby-3 text-n-ruby-11'
            "
          >
            {{
              item.status === 'success'
                ? t('PIPELINE.MESSAGES.SENT')
                : t('PIPELINE.MESSAGES.FAILED')
            }}
          </span>
        </div>
      </div>

      <!-- Ações -->
      <div class="flex justify-end">
        <Button variant="ghost" color="slate" size="sm" @click="handleClose">
          {{ t('PIPELINE.BULK_ACTIONS.CANCEL') }}
        </Button>
      </div>
    </div>

    <!-- Conteúdo Original -->
    <div v-else class="space-y-6">
      <!-- Lista de conversas e Switch de Filtros -->
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <h4 class="text-label font-medium text-n-slate-12">
            {{
              t('PIPELINE.BULK_ACTIONS.SELECTED_CONVERSATIONS', {
                count: selectedConversations.size,
                total: filteredItems.length,
              })
            }}
          </h4>
          <div class="flex items-center gap-4">
            <label class="flex items-center gap-2 text-label text-n-slate-11">
              <input v-model="showFilters" type="checkbox" class="toggle" />
              <span>{{ t('PIPELINE.BULK_ACTIONS.FILTER_ITEMS') }}</span>
            </label>
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="handleSelectAll"
            >
              {{
                selectedConversations.size === filteredItems.length
                  ? t('PIPELINE.BULK_ACTIONS.DESELECT_ALL')
                  : t('PIPELINE.BULK_ACTIONS.SELECT_ALL')
              }}
            </Button>
          </div>
        </div>

        <!-- Bloco de Filtros -->
        <div v-if="showFilters" class="space-y-4">
          <!-- Resumo dos Filtros -->
          <div class="bg-n-alpha-1 p-4 rounded-lg space-y-2">
            <div class="flex items-center justify-between">
              <h4 class="text-label font-medium text-n-slate-12">
                {{ t('PIPELINE.BULK_ACTIONS.FILTERS_ACTIVE') }}
              </h4>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                @click="
                  selectedConditions = {
                    status: '',
                    priority: '',
                    hasAttachments: false,
                    stage: '',
                    assignee: '',
                    lastActivity: '',
                    tags: [],
                    customFields: [],
                  }
                "
              >
                {{ t('PIPELINE.BULK_ACTIONS.CLEAR_FILTERS') }}
              </Button>
            </div>
            <div class="flex flex-wrap gap-2">
              <template v-for="(value, key) in selectedConditions" :key="key">
                <span
                  v-if="value && typeof value !== 'boolean'"
                  class="inline-flex items-center gap-1 px-2 py-1 text-label-small rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ key }}: {{ value }}
                  <button
                    class="text-n-slate-11 hover:text-n-slate-12"
                    :aria-label="$t('GENERAL_SETTINGS.FORM.LABELS.REMOVE')"
                    @click="selectedConditions[key] = ''"
                  >
                    {{ '×' }}
                  </button>
                </span>
                <span
                  v-else-if="value === true"
                  class="inline-flex items-center gap-1 px-2 py-1 text-label-small rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ key }}
                  <button
                    class="text-n-slate-11 hover:text-n-slate-12"
                    :aria-label="$t('GENERAL_SETTINGS.FORM.LABELS.REMOVE')"
                    @click="selectedConditions[key] = false"
                  >
                    {{ '×' }}
                  </button>
                </span>
              </template>
            </div>
          </div>

          <!-- Condições de Filtro -->
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="text-label font-medium text-n-slate-12">
                {{ t('PIPELINE.BULK_ACTIONS.FILTER_CONVERSATIONS') }}
              </h4>
              <span class="text-label-small text-n-slate-11">
                {{
                  t('PIPELINE.BULK_ACTIONS.FILTER_CONVERSATIONS_COUNT', {
                    count: filteredItems.length,
                  })
                }}
              </span>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-label text-n-slate-12 mb-2">
                  {{ t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.TITLE') }}
                </label>
                <select
                  v-model="selectedConditions.status"
                  class="field-base h-10 w-full"
                >
                  <option value="">
                    {{ t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.ALL') }}
                  </option>
                  <option value="open">
                    {{ t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.OPEN') }}
                  </option>
                  <option value="won">
                    {{ t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.WON') }}
                  </option>
                  <option value="lost">
                    {{ t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.LOST') }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-label text-n-slate-12 mb-2">
                  {{ t('PIPELINE.BULK_ACTIONS.PRIORITY.TITLE') }}
                </label>
                <select
                  v-model="selectedConditions.priority"
                  class="field-base h-10 w-full"
                >
                  <option value="">
                    {{ t('PIPELINE.BULK_ACTIONS.PRIORITY.ALL') }}
                  </option>
                  <option value="urgent">
                    {{ t('PIPELINE.BULK_ACTIONS.PRIORITY.URGENT') }}
                  </option>
                  <option value="high">
                    {{ t('PIPELINE.BULK_ACTIONS.PRIORITY.HIGH') }}
                  </option>
                  <option value="medium">
                    {{ t('PIPELINE.BULK_ACTIONS.PRIORITY.MEDIUM') }}
                  </option>
                  <option value="low">
                    {{ t('PIPELINE.BULK_ACTIONS.PRIORITY.LOW') }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-label text-n-slate-12 mb-2">
                  {{ t('PIPELINE.BULK_ACTIONS.STAGE.TITLE') }}
                </label>
                <select
                  v-model="selectedConditions.stage"
                  class="field-base h-10 w-full"
                >
                  <option value="">
                    {{ t('PIPELINE.BULK_ACTIONS.STAGE.ALL') }}
                  </option>
                  <option
                    v-for="stage in Object.keys(selectedPipeline?.stages || {})"
                    :key="stage"
                    :value="stage"
                  >
                    {{ selectedPipeline?.stages[stage]?.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-label text-n-slate-12 mb-2">
                  {{ t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.TITLE') }}
                </label>
                <select
                  v-model="selectedConditions.lastActivity"
                  class="field-base h-10 w-full"
                >
                  <option value="">
                    {{ t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.ANY') }}
                  </option>
                  <option value="today">
                    {{ t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.TODAY') }}
                  </option>
                  <option value="week">
                    {{ t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.WEEK') }}
                  </option>
                  <option value="month">
                    {{ t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.MONTH') }}
                  </option>
                </select>
              </div>
            </div>

            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2">
                <input
                  v-model="selectedConditions.hasAttachments"
                  type="checkbox"
                  class="rounded"
                />
                <span class="text-label text-n-slate-11">
                  {{ t('PIPELINE.BULK_ACTIONS.HAS_ATTACHMENTS') }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Lista de Conversas -->
        <div
          v-if="filteredItems.length > 0"
          class="max-h-60 overflow-y-auto border border-n-weak rounded-lg divide-y divide-n-weak"
        >
          <div
            v-for="item in filteredItems"
            :key="item.id"
            class="p-2 text-label hover:bg-n-alpha-1 flex items-center gap-2 cursor-pointer text-n-slate-12"
            @click="handleToggleConversation(item)"
          >
            <input
              type="checkbox"
              :checked="
                selectedConversations.has(item.item_details.conversation_id)
              "
              class="rounded"
              @click.stop
            />
            <span>
              {{ item.item_details.title }} -
              {{
                t('PIPELINE.BULK_ACTIONS.CONVERSATION_ID', {
                  id: item.item_details.conversation_id,
                })
              }}
            </span>
          </div>
        </div>
        <div v-else class="text-center text-label text-n-slate-11 py-4">
          {{ t('PIPELINE.BULK_ACTIONS.NO_CONVERSATIONS_AVAILABLE') }}
        </div>
      </div>

      <!-- Seleção de Template -->
      <div class="space-y-4">
        <h4 class="text-label font-medium text-n-slate-12">
          {{ t('PIPELINE.BULK_ACTIONS.SELECT_MESSAGE_TEMPLATE') }}
        </h4>

        <div v-if="loading" class="flex justify-center">
          <span class="loading-spinner" />
        </div>

        <div v-else class="space-y-4">
          <select
            v-model="selectedTemplate"
            class="field-base h-10 w-full"
            :class="{ 'border-n-ruby-9': !selectedTemplate }"
          >
            <option value="">
              {{ t('PIPELINE.BULK_ACTIONS.MESSAGE_TEMPLATE_SELECT') }}
            </option>
            <option
              v-for="template in templates"
              :key="template.id"
              :value="template"
            >
              {{ template.title }}
            </option>
          </select>

          <div v-if="selectedTemplate" class="p-4 bg-n-alpha-1 rounded-lg">
            <p class="text-body-main text-n-slate-11">
              {{ selectedTemplate.content }}
            </p>
          </div>
        </div>
      </div>

      <!-- Resumo -->
      <div class="p-4 bg-n-alpha-1 rounded-lg">
        <p class="text-body-main text-n-slate-11">
          {{
            t('PIPELINE.BULK_ACTIONS.MESSAGE_SUMMARY', {
              count: selectedConversations.size,
              total: items.length,
            })
          }}
        </p>
      </div>

      <!-- Ações -->
      <div class="flex justify-end gap-2">
        <Button variant="ghost" color="slate" size="sm" @click="handleClose">
          {{ $t('PIPELINE.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :is-loading="sendingMessage"
          :disabled="!selectedTemplate || selectedConversations.size === 0"
          @click="handleSend"
        >
          {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGES') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.toggle {
  @apply h-5 w-9 rounded-full appearance-none bg-n-alpha-2 transition-colors
    relative cursor-pointer checked:bg-n-brand;
}

.toggle:before {
  content: '';
  @apply absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-all;
}

.toggle:checked:before {
  @apply translate-x-4;
}
</style>
